import {
    Decimal,
    PreparedUpload,
    Receipt,
    Ulid,
} from "@kase/my-marathon-communication";
import { signal } from "@preact/signals";
import { store } from "~/core/store";
import { dateToApi } from "~/utils";
import { hasPatchChanges } from "../utils";
import {
    AddAttachmentParams,
    AddAttachmentRequest,
    CreateReceiptRequest,
    ExpenseReceipt,
    ReceiptSliceParams,
    UpdateReceiptRequest,
} from "./interfaces";

export const makeReceipt = ({
    connection,
    company,
    subscribers,
    expenses,
    uploadedReceiptIds,
}: ReceiptSliceParams) => {
    const view = (receipt: Receipt) => {
        const finalReceipt: ExpenseReceipt = {
            ...receipt,
            id: receipt.id.toString(),
            _id: receipt.id,
            attachments: signal([]),
            loading: signal(receipt.attachments.length > 0),
            _attachments: receipt.attachments.map((i) => Ulid.fromLoHi(i)),
            tag: signal(0),
        };

        return finalReceipt;
    };

    const create = async ({
        expense,
        amount,
        base_vat,
        comment,
        currency,
        date,
        expenseType,
        project,
        purchaseCode,
        quantity,
        rate,
        company_card,
    }: CreateReceiptRequest) => {
        const con = connection.value;
        const { currency: currencyApi } = store.api;
        const baseCurrency = currencyApi.base.peek();
        const finalCurrency = currency ? currency : baseCurrency?._id;

        if (!finalCurrency) {
            throw new Error("Couldn't create a receipt without a currency");
        }

        return con.expense.receipt.create({
            company: company.value,
            currency: finalCurrency,
            expense,
            amount: amount ? new Decimal(amount) : undefined,
            base_vat: base_vat ? new Decimal(base_vat) : undefined,
            rate: rate ? new Decimal(rate) : undefined,
            company_card,
            comment,
            date: date ? dateToApi(date) : undefined,
            expense_type: expenseType,
            project,
            purchase_code: purchaseCode,
            quantity: quantity ? new Decimal(quantity) : undefined,
        });
    };

    const updateChanged = (
        params: UpdateReceiptRequest,
        receipt?: ExpenseReceipt,
    ) => {
        const {
            amount,
            base_vat,
            comment,
            currency,
            date,
            expense_type,
            project,
            purchase_code,
            quantity,
            rate,
            company_card,
        } = params;
        const data = {
            amount:
                typeof amount === "number" ? new Decimal(amount) : undefined,
            base_vat:
                typeof base_vat === "number"
                    ? new Decimal(base_vat)
                    : undefined,
            rate: typeof rate === "number" ? new Decimal(rate) : undefined,
            comment,
            currency,
            date: date ? dateToApi(date) : undefined,
            expense_type,
            project,
            purchase_code,
            company_card,
            quantity:
                typeof quantity === "number"
                    ? new Decimal(quantity)
                    : undefined,
        };

        if (receipt && !hasPatchChanges(receipt, data)) {
            return false;
        }

        return data;
    };

    const update = async (
        params: UpdateReceiptRequest,
        receipt?: ExpenseReceipt,
    ) => {
        const con = connection.value;
        const data = updateChanged(params, receipt);

        if (!data) {
            return false;
        }

        return con.expense.receipt.update({
            id: params.id,
            company: company.value,
            ...data,
        });
    };

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;
        return con.expense.receipt.delete({ id, company: company.value });
    };

    const addAttachment = async (
        { blob, type, name, lastModified, receipt }: AddAttachmentRequest,
        { onProgress, process = true }: AddAttachmentParams = {},
    ) => {
        const con = connection.value;
        const prepared = await PreparedUpload.from(blob, {
            type,
            name,
            lastModified,
        });
        const upload = await con.expense.receipt.addAttachment(
            { company: company.value, receipt },
            prepared,
        );

        if (onProgress) {
            onProgress(0, upload.size);

            upload.progress.listen((v) => {
                onProgress(v, upload.size);

                return v !== upload.size;
            });
        }

        uploadedReceiptIds.push(receipt.toString());

        if (process && !(receipt.toString() in subscribers)) {
            const currentAttachments =
                expenses
                    .peek()
                    .flatMap((e) => e.receipts.peek())
                    .find((r) => r._id.equals(receipt))
                    ?.attachments.peek() ?? [];

            subscribers[receipt.toString()] = {
                skipIds: currentAttachments.map((a) => a.id),
            };
        }

        return upload.upload();
    };

    const removeAttachment = async (
        id: Readonly<Ulid>,
        receipt: Readonly<Ulid>,
    ) => {
        const con = connection.value;
        return con.expense.receipt.removeAttachment({
            id,
            receipt,
            company: company.value,
        });
    };

    return {
        view,
        create,
        update,
        remove,
        updateChanged,
        addAttachment,
        removeAttachment,
    };
};
